import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PrivacyPolicyView() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alreadyRead, setAlreadyRead] = useState(false);
  const [marking, setMarking] = useState(false);
  const [consensoChecked, setConsensoChecked] = useState(false);

  const fetchActivePolicy = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/privacy-policy/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicy(res.data);
      
      if (res.data && res.data.letture) {
        const currentUserId = JSON.parse(atob(token.split('.')[1])).user_id;
        const hasRead = res.data.letture.some(l => l.user_id === currentUserId);
        setAlreadyRead(hasRead);
      }
    } catch (err) {
      console.error('Errore caricamento policy:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivePolicy();
  }, [fetchActivePolicy]);

  const handleMarkAsRead = async () => {
    if (!policy || alreadyRead || !consensoChecked) return;
    
    setMarking(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/privacy-policy/${policy.id}/segna-letta`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAlreadyRead(true);
      toast.success('Consenso registrato con successo', {
        description: 'Il tuo consenso è stato acquisito e registrato con data e ora.',
        duration: 5000
      });
    } catch (err) {
      console.error('Errore registrazione consenso:', err);
      toast.error('Errore durante la registrazione del consenso', {
        description: 'Si è verificato un errore. Riprova o contatta l\'assistenza.'
      });
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-8">
          <div className="text-center text-sm text-gray-400">Caricamento...</div>
        </CardContent>
      </Card>
    );
  }

  if (!policy) {
    return (
      <div className="text-center py-12" data-testid="privacy-empty">
        <Shield className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-gray-500">Nessuna informativa privacy disponibile</p>
        <p className="text-xs text-gray-400 mt-1">Le informative verranno pubblicate dall'amministratore</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="privacy-policy-view">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-cyan-600" />
              <div>
                <CardTitle className="text-base">{policy.title}</CardTitle>
                <CardDescription className="text-xs">
                  Versione {policy.version} — Aggiornata il {new Date(policy.updated_at).toLocaleDateString('it-IT')}
                </CardDescription>
              </div>
            </div>
            {alreadyRead && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-700">Consenso Acquisito</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="prose prose-sm max-w-none text-sm"
            dangerouslySetInnerHTML={{ __html: policy.content }}
          />

          {alreadyRead && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-start gap-2.5 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">Consenso già acquisito</p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Hai letto e fornito il tuo consenso. Il consenso è registrato nel sistema.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!alreadyRead && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Consenso Obbligatorio</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Leggi attentamente l'informativa e fornisci il tuo consenso esplicito.
                    </p>
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-3 bg-white rounded-lg border border-gray-200 hover:border-cyan-400 transition-colors">
                  <input
                    type="checkbox"
                    checked={consensoChecked}
                    onChange={(e) => setConsensoChecked(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="text-sm text-gray-800">
                    Dichiaro di aver letto, compreso e <strong>accetto</strong> l'informativa sulla privacy e presto il mio consenso al trattamento dei dati personali.
                  </span>
                </label>

                <Button
                  onClick={handleMarkAsRead}
                  disabled={marking || !consensoChecked}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed gap-1.5"
                >
                  {marking ? (
                    'Registrazione in corso...'
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Conferma Consenso
                    </>
                  )}
                </Button>

                <p className="text-[11px] text-gray-400">
                  Il consenso sarà registrato con data e ora. Potrai revocarlo contattando l'amministratore.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
